import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import Footer from '../components/Footer';
import '../styles/Legal.css';

const UPDATED = '01.06.2025';

const content = {
  tr: {
    badge: 'Çerezler',
    title: 'Çerez Politikası',
    updated: `Son güncelleme: ${UPDATED}`,
    intro: `Bu sayfa, vitex.app tarafından hangi çerezlerin kullanıldığını ve bunların nasıl yönetileceğini açıklamaktadır.`,
    whatTitle: 'Çerez Nedir?',
    whatBody: `Çerezler, bir web sitesini ziyaret ettiğinizde tarayıcınıza yerleştirilen küçük metin dosyalarıdır.
    Oturum bilgilerini tutmak, tercihlerinizi hatırlamak ve siteye nasıl eriştiğinizi anlamak için kullanılırlar.`,
    tableTitle: 'Kullandığımız Çerezler',
    tableHeaders: ['Çerez / Depolama Anahtarı', 'Tür', 'Amaç', 'Süre'],
    cookies: [
      ['vitex.token', 'Zorunlu', 'JWT erişim tokeni — kimlik doğrulama', 'Oturum'],
      ['vitex.refreshToken', 'Zorunlu', 'Yenileme tokeni — oturumu açık tutmak', '7 gün'],
      ['vitex.lang', 'İşlevsel', 'Dil tercihi (TR/EN)', 'Kalıcı'],
      ['vitex.cookie-consent', 'Zorunlu', 'Çerez onayı kaydı', 'Kalıcı'],
      ['Iyzico çerezleri', 'Ödeme', '3D Secure ödeme doğrulama akışı', 'Oturum'],
    ],
    noteTitle: 'Önemli Not',
    noteBody: `ViTex; Google Analytics, Facebook Pixel veya herhangi bir reklam ağı çerezi kullanmamaktadır.
    Yukarıdaki çerezler dışında üçüncü taraf takip çerezi bulunmamaktadır.`,
    manageTitle: 'Çerezleri Yönetmek',
    manageBody: `Tarayıcı ayarlarınızdan çerezleri devre dışı bırakabilirsiniz. Ancak zorunlu çerezlerin
    engellenmesi durumunda oturum açma ve ödeme gibi temel işlevler çalışmayabilir.
    Onayınızı değiştirmek için tarayıcınızın localStorage bölümünden "vitex.cookie-consent" anahtarını
    silmeniz yeterlidir; sayfa yenilendiğinde banner yeniden gösterilecektir.`,
    contactTitle: 'İletişim',
    contactEmail: 'vitex.app@gmail.com',
  },
  en: {
    badge: 'Cookies',
    title: 'Cookie Policy',
    updated: `Last updated: ${UPDATED}`,
    intro: `This page explains which cookies vitex.app uses and how you can manage them.`,
    whatTitle: 'What Are Cookies?',
    whatBody: `Cookies are small text files placed on your device when you visit a website. They are used to
    maintain session state, remember preferences, and understand how the site is accessed.`,
    tableTitle: 'Cookies We Use',
    tableHeaders: ['Cookie / Storage Key', 'Type', 'Purpose', 'Duration'],
    cookies: [
      ['vitex.token', 'Strictly Necessary', 'JWT access token — authentication', 'Session'],
      ['vitex.refreshToken', 'Strictly Necessary', 'Refresh token — keep session alive', '7 days'],
      ['vitex.lang', 'Functional', 'Language preference (TR/EN)', 'Persistent'],
      ['vitex.cookie-consent', 'Strictly Necessary', 'Records cookie consent choice', 'Persistent'],
      ['Iyzico cookies', 'Payment', '3D Secure payment verification flow', 'Session'],
    ],
    noteTitle: 'Important Note',
    noteBody: `ViTex does not use Google Analytics, Facebook Pixel, or any advertising network cookies.
    There are no third-party tracking cookies beyond those listed above.`,
    manageTitle: 'Managing Cookies',
    manageBody: `You can disable cookies in your browser settings. However, blocking strictly necessary cookies
    may prevent core features such as login and payment from working correctly.
    To withdraw your consent, delete the "vitex.cookie-consent" key from your browser's localStorage;
    the banner will reappear on the next page load.`,
    contactTitle: 'Contact',
    contactEmail: 'vitex.app@gmail.com',
  },
};

export default function CookiePolicy() {
  const { lang, t } = useI18n();
  const c = content[lang] || content.tr;

  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-inner">
          <Link to="/" className="legal-back-link">← {t('common.backToHome')}</Link>
          <span className="legal-header-title">{t('legal.cookies')}</span>
        </div>
      </header>

      <main className="legal-content">
        <span className="legal-badge">{c.badge}</span>
        <h1 className="legal-title">{c.title}</h1>
        <p className="legal-meta">{c.updated}</p>

        <div className="legal-section">
          <p>{c.intro}</p>
        </div>

        <div className="legal-section">
          <h2>{c.whatTitle}</h2>
          <p>{c.whatBody}</p>
        </div>

        <div className="legal-section">
          <h2>{c.tableTitle}</h2>
          <table className="legal-table">
            <thead>
              <tr>
                {c.tableHeaders.map((h, i) => <th key={i}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {c.cookies.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="legal-section">
          <h2>{c.noteTitle}</h2>
          <div className="legal-highlight-box">
            <p>{c.noteBody}</p>
          </div>
        </div>

        <div className="legal-section">
          <h2>{c.manageTitle}</h2>
          <p>{c.manageBody}</p>
        </div>

        <div className="legal-section">
          <h2>{c.contactTitle}</h2>
          <div className="legal-contact-box">
            <p><strong>Email:</strong>{' '}
              <a href={`mailto:${c.contactEmail}`}>{c.contactEmail}</a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
